import styled, { keyframes } from 'styled-components';

import { fadeIn } from '../../constants/animations';
import { zIndexes, colors, fontWeights, fontSizes } from '../../theme';
import { hexWithOpacity } from '../../utils';

import {
  ActionSheetVariableNames,
  fadeInDuration,
  fadeOutDelay,
  fadeOutDuration,
  popupAndSlideInDuration,
  slideOutDuration,
} from './constants';

const popAndSlideFromBottom = keyframes`
  0% {
    transform: scale(0);
    transform: translateY(100%);
  }

  40% {
    transform: scale(1);
    transform: translateY(100%);
  }

  100% {
    transform: scale(1);
    transform: translateY(0);
  }
`;

export const ActionSheetRoot = styled.div`
  box-sizing: border-box;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: ${zIndexes.actionSheet};
  display: flex;
  width: 100%;
  height: 100%;
`;

export const Overlay = styled.div<{ $isExpanded: boolean }>`
  position: absolute;
  box-sizing: border-box;
  inset: 0;
  background: ${hexWithOpacity(colors.navy, 0.8)};

  opacity: ${({ $isExpanded }) => !$isExpanded && '0'};
  transition: ${({ $isExpanded }) =>
    !$isExpanded && `opacity ${fadeOutDuration}ms ease-out ${fadeOutDelay}ms`};
  animation: ${fadeInDuration}ms ease-out ${fadeIn};
`;

export const Sheet = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  box-sizing: border-box;
  z-index: 1;
  display: grid;

  grid-template:
    'header' auto
    'content' minmax(0, 1fr)
    'footer' var(${ActionSheetVariableNames.FooterHeight}, 0)
    / minmax(0, 1fr);

  flex: 1 1 auto;

  margin-top: 24px;
  padding: 32px 0 0;
  overflow: hidden;
  background-color: ${colors.pearl};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;

  transform: ${({ $isExpanded }) => !$isExpanded && 'translateY(100%)'};
  transition: ${({ $isExpanded }) =>
    !$isExpanded && `transform ${slideOutDuration}ms ease-out`};
  animation: ${popAndSlideFromBottom} ${popupAndSlideInDuration}ms ease-in;

  will-change: transform;
`;

export const SheetHeader = styled.div`
  grid-area: header;
  padding-bottom: 20px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 0;
  flex-shrink: 0;
`;

export const HeaderLeft = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: 36px;
  padding-left: 32px;
  align-items: center;
`;

export const Title = styled.h3`
  position: absolute;
  box-sizing: border-box;
  left: 50%;
  color: ${colors.navy};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.xxl};
  transform: translateX(-50%);
  margin: 0;
`;

export const SheetContent = styled.div`
  grid-area: content;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const SheetFooter = styled.div`
  position: absolute;
  box-sizing: border-box;
  z-index: 2;
  bottom: 0;
  left: 0;
  width: 100%;
  box-shadow: 2px 2px 10px ${hexWithOpacity(colors.mouse, 0.3)};
`;
